// The management statement and system description (US 9394) are rich text: a small subset of HTML that
// the app, the Word import and the Word report understand (web/app/Project/Reports/RichText.php reads the
// same format):
//   blocks:                      <p>, <h2>, <h3>, <ul>, <ol>, <ol type="a"> and <ol type="i">
//   in a block or list item:     text, <b>, <i>, <u> and <br>
//   a list item ends with its nested lists, at most MAX_LIST_DEPTH levels deep
// Everything else is dropped, but its text is kept. A value without any block tag is plain text from before
// US 9394, where every line is a paragraph and <b>, <i> and <u> are formatting (the format of US 9390).
//
// Task text fields (notes, testing notes, control description, performed test, conclusion and the client's
// comment) use the same format without headers, with { headings: false, compact: true }: a text without lists
// is still stored in the line format of US 9390, so it reads as before everywhere else.

export const MAX_LIST_DEPTH = 3;

const BLOCK_TAG = /<(p|h2|h3|ul|ol)[\s>]/i;
// Elements whose content is never text, e.g. in HTML pasted from Word
const SKIP = ['script', 'style', 'head', 'title', 'meta', 'link', 'template', 'noscript', 'xml', 'img', 'svg', 'object', 'iframe', 'video', 'audio', 'canvas'];
// Elements that start a new line
const BLOCKS = ['p', 'div', 'blockquote', 'pre', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'caption',
  'section', 'article', 'header', 'footer', 'main', 'nav', 'aside', 'address', 'figure', 'figcaption', 'dl', 'dt', 'dd', 'center', 'hr'];
const HEADINGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
// Header levels from other sources (a pasted page) become Header 2 or Header 3
const DEFAULT_HEADINGS = { h1: 'h2', h2: 'h2', h3: 'h3', h4: 'h3', h5: 'h3', h6: 'h3' };
// The default type of a numbered list one level down
export const NESTED_LIST_KIND = { 1: 'a', a: 'i', i: '1' };

// Without headers, every header level is a normal paragraph
const NO_HEADINGS = { h1: 'p', h2: 'p', h3: 'p', h4: 'p', h5: 'p', h6: 'p' };
// Text that would be read as markup in the line format
const LINE_MARKUP = /<\/?(b|i|u|h3)>|<(p|h2|h3|ul|ol)[\s>]/i;

const NO_STYLE = { b: false, i: false, u: false };

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function tagOf(node) {
  return node.nodeName.toLowerCase();
}

// bullet, 1, a or i
export function listKind(list) {
  if (tagOf(list) === 'ul') {
    return 'bullet';
  }
  const type = (list.getAttribute('type') || '').toLowerCase();
  const style = (list.style && list.style.listStyleType) || '';
  if (type === 'a' || style.includes('alpha') || style.includes('latin')) {
    return 'a';
  }
  if (type === 'i' || style.includes('roman')) {
    return 'i';
  }
  return '1';
}

function styleOf(element, style) {
  const tag = tagOf(element);
  const css = element.style || {};
  const weight = css.fontWeight || '';
  return {
    b: style.b || tag === 'b' || tag === 'strong' || weight === 'bold' || parseInt(weight, 10) >= 600,
    i: style.i || tag === 'i' || tag === 'em' || css.fontStyle === 'italic',
    u: style.u || tag === 'u' || (css.textDecoration || css.textDecorationLine || '').includes('underline')
  };
}

// Text as it shows in HTML: runs of whitespace are one space (a non-breaking space stays)
function addText(inline, text, style) {
  let value = text.replace(/[ \t\n\r\f]+/g, ' ');
  const last = inline[inline.length - 1];
  if (value.startsWith(' ') && (!last || last.br || last.text.endsWith(' '))) {
    value = value.slice(1);
  }
  if (!value) {
    return;
  }
  if (last && !last.br && last.b === style.b && last.i === style.i && last.u === style.u) {
    last.text += value;
  } else {
    inline.push({ text: value, b: style.b, i: style.i, u: style.u });
  }
}

function addBreak(inline) {
  inline.push({ br: true });
}

// The text of an element as one paragraph; blocks inside it become line breaks
function parseInline(element, style, inline = []) {
  element.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      addText(inline, child.nodeValue, style);
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE || SKIP.includes(tagOf(child))) {
      return;
    }
    const tag = tagOf(child);
    if (tag === 'br') {
      addBreak(inline);
      return;
    }
    const block = BLOCKS.includes(tag) || HEADINGS.includes(tag) || ['li', 'ul', 'ol'].includes(tag);
    if (block && inline.length && !inline[inline.length - 1].br) {
      addBreak(inline);
    }
    parseInline(child, styleOf(child, style), inline);
  });
  return inline;
}

function parseList(element, style, options) {
  const list = { type: 'list', kind: listKind(element), items: [] };
  const lastItem = () => {
    if (!list.items.length) {
      list.items.push({ inline: [], lists: [] });
    }
    return list.items[list.items.length - 1];
  };

  element.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.nodeValue.trim()) {
        addText(lastItem().inline, child.nodeValue, style);
      }
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE || SKIP.includes(tagOf(child))) {
      return;
    }
    const tag = tagOf(child);
    if (tag === 'ul' || tag === 'ol') {
      // A list right inside a list, as Chrome makes it when a list item is indented, belongs to the item before it
      lastItem().lists.push(parseList(child, style, options));
    } else if (tag === 'li') {
      list.items.push(parseItem(child, styleOf(child, style), options));
    } else {
      parseInline(child, styleOf(child, style), lastItem().inline);
    }
  });
  return list;
}

function parseItem(element, style, options) {
  const item = { inline: [], lists: [] };
  element.childNodes.forEach((child) => {
    if (child.nodeType === Node.ELEMENT_NODE && ['ul', 'ol'].includes(tagOf(child))) {
      item.lists.push(parseList(child, style, options));
    } else if (child.nodeType === Node.TEXT_NODE) {
      addText(item.inline, child.nodeValue, style);
    } else if (child.nodeType === Node.ELEMENT_NODE && !SKIP.includes(tagOf(child))) {
      if (tagOf(child) === 'br') {
        addBreak(item.inline);
        return;
      }
      if (BLOCKS.includes(tagOf(child)) && item.inline.length && !item.inline[item.inline.length - 1].br) {
        addBreak(item.inline);
      }
      parseInline(child, styleOf(child, style), item.inline);
    }
  });
  return item;
}

function headingMap(options) {
  if (options.headings === false) {
    return NO_HEADINGS;
  }
  return options.headings || DEFAULT_HEADINGS;
}

function parseBlocks(root, options) {
  const headings = headingMap(options);
  const blocks = [];
  let loose = null;
  const flush = () => {
    if (loose) {
      blocks.push({ type: 'p', inline: loose });
      loose = null;
    }
  };

  function walk(node, style) {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        // Whitespace between blocks isn't a paragraph
        if (loose || child.nodeValue.trim()) {
          addText(loose || (loose = []), child.nodeValue, style);
        }
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE || SKIP.includes(tagOf(child))) {
        return;
      }
      const tag = tagOf(child);
      if (tag === 'br') {
        addBreak(loose || (loose = []));
      } else if (HEADINGS.includes(tag)) {
        flush();
        blocks.push({ type: headings[tag] || 'p', inline: parseInline(child, styleOf(child, style)) });
      } else if (tag === 'ul' || tag === 'ol') {
        flush();
        blocks.push(parseList(child, style, options));
      } else if (tag === 'li') {
        // A list item outside a list
        flush();
        blocks.push({ type: 'list', kind: 'bullet', items: [parseItem(child, styleOf(child, style), options)] });
      } else if (BLOCKS.includes(tag)) {
        flush();
        if (tag === 'p' || tag === 'div') {
          // An empty line is <p><br></p> (or <div><br></div> in the editor)
          const before = blocks.length;
          walk(child, styleOf(child, style));
          if (blocks.length === before && !loose) {
            loose = [];
          }
        } else {
          walk(child, styleOf(child, style));
        }
        flush();
      } else {
        walk(child, styleOf(child, style));
      }
    });
  }

  walk(root, NO_STYLE);
  flush();
  return tidyBlocks(blocks);
}

function tidyInline(inline) {
  // A trailing <br> is only there so the caret can sit on an empty last line
  while (inline.length && inline[inline.length - 1].br) {
    inline.pop();
  }
  const last = inline[inline.length - 1];
  if (last) {
    last.text = last.text.replace(/ +$/, '');
    if (!last.text) {
      inline.pop();
    }
  }
  return inline;
}

function tidyList(list, depth) {
  const items = [];
  list.items.forEach((item) => {
    tidyInline(item.inline);
    let lists = item.lists.map((nested) => tidyList(nested, depth + 1)).filter((nested) => nested.items.length);
    let flattened = [];
    if (depth >= MAX_LIST_DEPTH) {
      // Deeper levels move up to the deepest level allowed
      lists.forEach((nested) => {
        flattened = flattened.concat(nested.items);
      });
      lists = [];
    }
    if (item.inline.length || lists.length) {
      items.push({ inline: item.inline, lists });
    }
    items.push(...flattened);
  });
  return { type: 'list', kind: list.kind, items };
}

function tidyBlocks(blocks) {
  const tidy = [];
  blocks.forEach((block) => {
    if (block.type === 'list') {
      const list = tidyList(block, 1);
      if (list.items.length) {
        tidy.push(list);
      }
      return;
    }
    tidyInline(block.inline);
    if (block.type !== 'p' && !block.inline.length) {
      return;
    }
    tidy.push(block);
  });
  // Empty paragraphs at the start or end
  while (tidy.length && tidy[0].type === 'p' && !tidy[0].inline.length) {
    tidy.shift();
  }
  while (tidy.length && tidy[tidy.length - 1].type === 'p' && !tidy[tidy.length - 1].inline.length) {
    tidy.pop();
  }
  return tidy;
}

function inlineToHtml(inline) {
  return inline.map((run) => {
    if (run.br) {
      return '<br>';
    }
    let html = escapeHtml(run.text);
    if (run.u) {
      html = '<u>' + html + '</u>';
    }
    if (run.i) {
      html = '<i>' + html + '</i>';
    }
    if (run.b) {
      html = '<b>' + html + '</b>';
    }
    return html;
  }).join('');
}

function listToHtml(list) {
  const open = list.kind === 'bullet' ? '<ul>' : (list.kind === '1' ? '<ol>' : '<ol type="' + list.kind + '">');
  const close = list.kind === 'bullet' ? '</ul>' : '</ol>';
  return open + list.items.map((item) => '<li>' + inlineToHtml(item.inline) + item.lists.map(listToHtml).join('') + '</li>').join('') + close;
}

function blocksToHtml(blocks) {
  return blocks.map((block) => {
    if (block.type === 'list') {
      return listToHtml(block);
    }
    const inline = inlineToHtml(block.inline);
    return '<' + block.type + '>' + (inline || '<br>') + '</' + block.type + '>';
  }).join('');
}

function parseHtml(html) {
  return new DOMParser().parseFromString(html, 'text/html').body;
}

// The line format: every line a paragraph, a line wrapped in <h3> a header, and <b>, <i> and <u> formatting
function plainTextToHtml(text) {
  return text.split(/\r?\n/).map((line) => {
    const heading = line.match(/^<h3>(.*)<\/h3>$/);
    const tag = heading ? 'h3' : 'p';
    const html = escapeHtml(heading ? heading[1] : line).replace(/&lt;(\/?)(b|i|u)&gt;/g, '<$1$2>');
    return '<' + tag + '>' + (html || '<br>') + '</' + tag + '>';
  }).join('');
}

function parseValue(value, options) {
  if (!value) {
    return [];
  }
  return parseBlocks(parseHtml(BLOCK_TAG.test(value) ? value : plainTextToHtml(value)), options);
}

function onlyParagraphs(blocks) {
  return blocks.every((block) => block.type === 'p');
}

function inlineToLine(inline) {
  return inline.map((run) => {
    if (run.br) {
      return '\n';
    }
    let text = run.text;
    if (run.u) {
      text = '<u>' + text + '</u>';
    }
    if (run.i) {
      text = '<i>' + text + '</i>';
    }
    if (run.b) {
      text = '<b>' + text + '</b>';
    }
    return text;
  }).join('');
}

function blocksToValue(blocks, options) {
  // Without lists, a task text keeps the line format, unless its text would be read as markup there
  if (options.compact && onlyParagraphs(blocks)) {
    const hasMarkupText = blocks.some((block) => block.inline.some((run) => !run.br && LINE_MARKUP.test(run.text)));
    if (!hasMarkupText) {
      return blocks.map((block) => inlineToLine(block.inline)).join('\n');
    }
  }
  return blocksToHtml(blocks);
}

// Element (the editor, or a parsed document) -> stored value.
// options.headings maps h1-h6 to h2, h3 or p (e.g. from rankHeadings()), or is false for no headers at all.
// options.compact keeps a text without lists in the line format.
export function richTextFromElement(element, options = {}) {
  return blocksToValue(parseBlocks(element, options), options);
}

// Any HTML (pasted, or from a Word document) -> stored value
export function richTextFromHtml(html, options = {}) {
  return richTextFromElement(parseHtml(html), options);
}

// Stored value -> safe HTML to show. Everything except the allowed tags is escaped or dropped.
// With options.compact, a text without lists is shown as lines (with <br>) instead of paragraphs.
export function richTextToHtml(value, options = {}) {
  const blocks = parseValue(value, options);
  if (options.compact && onlyParagraphs(blocks)) {
    return blocks.map((block) => inlineToHtml(block.inline)).join('<br>');
  }
  return blocksToHtml(blocks);
}

// Stored value -> text without formatting, e.g. for a shortened preview. List items start with "- ".
export function richTextToPlainText(value, options = {}) {
  const lines = [];
  const text = (inline) => inline.map((run) => (run.br ? '\n' : run.text)).join('');
  const addList = (list, depth) => {
    list.items.forEach((item) => {
      lines.push('  '.repeat(depth) + '- ' + text(item.inline));
      item.lists.forEach((nested) => addList(nested, depth + 1));
    });
  };
  parseValue(value, options).forEach((block) => {
    if (block.type === 'list') {
      addList(block, 0);
    } else {
      lines.push(text(block.inline));
    }
  });
  return lines.join('\n');
}

// The header levels a document actually uses, ranked: the top one becomes Header 2 and every other one
// Header 3, so a document that starts at Heading 2 or skips a level still gets both. merged lists the
// levels (1-6) that ended up together as Header 3 when there were more than two.
export function rankHeadings(root) {
  const used = HEADINGS.filter((tag) => root.querySelector(tag));
  const headings = {};
  used.forEach((tag, index) => {
    headings[tag] = index === 0 ? 'h2' : 'h3';
  });
  const merged = used.length > 2 ? used.slice(1).map((tag) => parseInt(tag.slice(1), 10)) : [];
  return { headings, merged };
}
